import React, { useState } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
  Container,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/system";
import Navbar from "src/Component/Navbar";
import Footer from "src/Component/Footer";
import LetsGetStarted from "src/Component/LetsGetStarted";
import {
  RegisterYourself,
  Location,
  ProgramAndPreferredYear,
  FieldOfStudy,
  AdmissionRequirements,
} from "./components/StepComponents";

import forwardarrow from "../asset/universityfinder/forwardArrow.png";
import backwardArrow from "../asset/universityfinder/backwardArrow.png";

const steps = [
  "Create Your Profile",
  "Programs & Destinations",
  "Academic Plan",
  "Field of Study",
  "Prerequisites",
];

const CustomStepLabel = styled(StepLabel)(({ theme, active }) => ({
  "& .MuiStepIcon-root": {
    color: active ? "#00999E" : "#fff",
    border: "2px solid",
    borderColor: active ? "#00999E" : "#000",
    backgroundColor: active ? "#00999E" : "transparent",
    borderRadius: "50%",
    width: "35px",
    height: "35px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "& .MuiStepIcon-text": {
    fill: active ? "#fff" : "#000",
    fontWeight: 550,
  },
  "& .MuiStepLabel-label": {
    marginTop: theme.spacing(1),
    color: "#000",
    textAlign: "center",
  },
}));

export default function UniversityFinder() {
  const [filters, setFilters] = useState({
    country: "",
    pursue: "",
    year: "",
    intake: "",
    duration: "",
    tutionfee: "",
    courses: "",
    admission: "",
    qualification: "",
    scholarship: "",
    name: "",
    phone: "",
    email: "",
    fieldOfInterestValue: [],
  });
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const navigate = useNavigate();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        if (!filters.name || !filters.phone || !filters.email) {
          showSnackbar("Please fill in Name, Phone, and Email.");
          return false;
        }
        return true;
      case 1:
        if (!filters.courses) {
          showSnackbar("Please select a field of interest.");
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
      scrollToTop();
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
      scrollToTop();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        type: "submit",
        name: filters.name,
        phone: filters.phone,
        email: filters.email,
        courses: filters.courses,
        ...(filters.country && { countryId: filters.country }),
        ...(filters.pursue && { pursue: filters.pursue }),
        ...(filters.year && { year: filters.year }),
        ...(filters.tutionfee && { tutionFee: filters.tutionfee }),
        ...(filters.duration && { duration: filters.duration }),
        ...(filters.intake && { intake: filters.intake }),
        ...(filters.admission && { admissionRequirement: filters.admission }),
        ...(filters.scholarship && {
          scholarAvailability: filters.scholarship,
        }),
        ...(filters.qualification && {
          highestQualification: filters.qualification,
        }),
      };

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}university/universityFilter`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Submission failed.");

      const data = await response.json();
      navigate("/study-abroad/universities/view", {
        state: { data: data.data.formattedData, data2: data.data.filterData },
      });
      scrollToTop();
    } catch (error) {
      showSnackbar("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return <RegisterYourself filters={filters} setFilters={setFilters} />;
      case 1:
        return <Location filters={filters} setFilters={setFilters} />;
      case 2:
        return (
          <ProgramAndPreferredYear filters={filters} setFilters={setFilters} />
        );
      case 3:
        return <FieldOfStudy filters={filters} setFilters={setFilters} />;
      case 4:
        return (
          <AdmissionRequirements filters={filters} setFilters={setFilters} />
        );
      default:
        return <Typography>Unknown Step</Typography>;
    }
  };

  return (
    <Box id="university-finder-section">
      <Navbar />

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        autoHideDuration={4000}
      >
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          width: "100%",
          height: "100px",
          padding: "20px 0",
          bgcolor: "#effdff",
        }}
      >
        {!isXs ? (
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label} completed={false}>
                <CustomStepLabel icon={index + 1} active={activeStep === index}>
                  {label}
                </CustomStepLabel>
              </Step>
            ))}
          </Stepper>
        ) : (
          <Box
            textAlign="center"
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Box
              sx={{
                height: "40px",
                width: "40px",
                border: "2px solid #00999E",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "34px",
                  height: "34px",
                  bgcolor: "#00999E",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6" color="#fff">
                  {activeStep + 1}
                </Typography>
              </Box>
            </Box>
            <Typography variant="h6">{steps[activeStep]}</Typography>
          </Box>
        )}
      </Box>

      <Box>
        <Box mt={2}>{renderStepContent(activeStep)}</Box>
        <Container>
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button
              variant="contained"
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={
                <img
                  src={backwardArrow}
                  alt="Back"
                  style={{ width: 50, height: 15 }}
                />
              }
              sx={{
                backgroundColor: activeStep === 0 ? "#e0e0e0" : "#00999E",
                color: activeStep === 0 ? "#a0a0a0" : "#fff",
                "&:hover": {
                  backgroundColor: activeStep === 0 ? "#e0e0e0" : "#007b8f",
                },
              }}
            >
              Previous
            </Button>

            <LoadingButton
              variant="contained"
              onClick={
                activeStep === steps.length - 1 ? handleSubmit : handleNext
              }
              endIcon={
                activeStep !== steps.length - 1 ? (
                  <img
                    src={forwardarrow}
                    alt="Next"
                    style={{ width: 50, height: 15 }}
                  />
                ) : null
              }
              sx={{
                backgroundColor: "#00999E",
                color: "#fff",
                "&:hover": { backgroundColor: "#007b8f" },
              }}
              loading={isSubmitting}
            >
              {activeStep === steps.length - 1 ? "Submit" : "Next"}
            </LoadingButton>
          </Box>
        </Container>
      </Box>

      <LetsGetStarted marginTop="50px" />
      <Footer />
    </Box>
  );
}
